import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { useEventTypes } from "~/lib/tanstack-query/hooks/useEventTypes";
import { NumberOfGuests } from "~/types/search";
import { Spinner } from "../ui/spinner";
import { PinIcon } from "~/icons/PinIcon";
import { UserIcon } from "~/icons/UserIcon";
import { AmountFormatted } from "../AmountFormatted";
import { useState, useRef, useEffect } from "react";
import { useDebounce } from "~/lib/hooks/useDebounce";
import {
  PLACES_AUTOCOMPLETE_DEBOUNCE_MS,
  PLACES_AUTOCOMPLETE_MIN_CHARS,
  shouldFetchPlacesAutocomplete,
} from "~/lib/places-autocomplete-constants";
import { usePlacesAutocomplete } from "~/lib/tanstack-query/hooks/usePlacesAutocomplete";
import { addLocationSelection } from "~/lib/search/location-place-ids";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverAnchor } from "../ui/popover";
import { cn } from "~/lib/utils";
import { eventTypesSequence } from "~/utils/sequences";

export const NumberOfGuestsOptions = {
  any: "beliebig",
  "gte-10": "mind. 10",
  "gte-25": "mind. 25",
  "gte-50": "mind. 50",
  "gte-100": "mind. 100",
  "gte-200": "mind. 200",
  "gte-500": "mind. 500",
  "gte-1000": "mind. 1000",
};

type FormParams = {
  location: string[];
  placeIds?: string[];
  guests: NumberOfGuests;
  type: number | null;
};

export const SearchBar = ({
  className,
  size = "small",
  numberOfMatchingLocations,
  isPending,
  location,
  placeIds,
  guests,
  type,
  setLocation,
  setPlaceIds,
  setLocationSearch,
  setGuests,
  setType,
  onSubmit,
}: {
  className?: string;
  size?: "small" | "large";
  numberOfMatchingLocations: number;
  isPending: boolean;
  location: string[];
  placeIds?: string[];
  guests: NumberOfGuests;
  type: number | null;
  setLocation?: (location: string[]) => void;
  setPlaceIds?: (placeIds: string[] | undefined) => void;
  /** Atomically update location + placeIds (avoids stale state when both change). */
  setLocationSearch?: (location: string[], placeIds?: string[]) => void;
  setGuests?: (guests: NumberOfGuests) => void;
  setType?: (type: number | null) => void;
  onSubmit?: (params: FormParams) => void;
}) => {
  const { data: eventTypes, isPending: isPendingEventTypes } =
    useEventTypes(eventTypesSequence);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  // Use last location for display, or empty string if no locations
  const [inputValue, setInputValue] = useState(
    location && location.length > 0 ? location[location.length - 1] : "",
  );
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedInputValue = useDebounce(
    inputValue,
    PLACES_AUTOCOMPLETE_DEBOUNCE_MS,
  );

  // Only make API call when input is focused and user has interacted.
  // Focus/interaction are state (not refs) so focusing a prefilled location
  // re-renders and enables autocomplete without requiring a keystroke.
  // No Autocomplete session tokens here: SearchBar only needs placeId, and
  // closing sessions with Place Details is far more expensive than per-request
  // Autocomplete on this high-traffic path.
  const shouldFetchSuggestions = shouldFetchPlacesAutocomplete(
    isInputFocused,
    hasUserInteracted,
  );
  // The query key stays on the typed text and only fetching is gated. Blanking
  // the key on blur would drop the suggestion list mid-click: the input blurs on
  // mousedown, so the item under the cursor would unmount before it is clicked.
  const { data: autocompleteData, isFetching: isPendingSuggestions } =
    usePlacesAutocomplete(debouncedInputValue, {
      source: "search_bar",
      profile: "regions",
      enabled: shouldFetchSuggestions,
    });
  const suggestions = autocompleteData?.predictions ?? [];

  // Sync inputValue with location prop (use last location)
  useEffect(() => {
    const lastLocation =
      location && location.length > 0 ? location[location.length - 1] : "";
    // Only sync if the location prop differs from current inputValue
    // Don't sync if input is focused (user is actively typing)
    // This prevents resetting when user is typing
    if (
      lastLocation !== inputValue &&
      document.activeElement !== inputRef.current
    ) {
      setInputValue(lastLocation);
      // Only reset interaction flag if input is not focused (external change, e.g., from URL params)
      setHasUserInteracted(false);
      setIsInputFocused(false);
    }
  }, [location, inputValue]);

  // Open autocomplete when suggestions arrive (after debounce completes)
  // Only if user has interacted and input is focused
  useEffect(() => {
    if (!isInputFocused || !hasUserInteracted) {
      return;
    }

    if (debouncedInputValue.length < PLACES_AUTOCOMPLETE_MIN_CHARS) {
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
      return;
    }

    // Open autocomplete when we have suggestions or are loading them
    if (suggestions.length > 0 || isPendingSuggestions) {
      setAutocompleteOpen(true);
      setSelectedIndex(-1); // Reset selection when suggestions change
    }
  }, [
    suggestions,
    debouncedInputValue,
    isPendingSuggestions,
    isInputFocused,
    hasUserInteracted,
  ]);

  // The effect above deliberately leaves the popover open while the input is
  // blurred, because blur fires on mousedown and closing it would swallow the
  // click on a suggestion. Once there is nothing left to click, though, an open
  // popover is a dead end the user cannot dismiss by selecting anything.
  useEffect(() => {
    if (
      autocompleteOpen &&
      !isInputFocused &&
      !isPendingSuggestions &&
      suggestions.length === 0
    ) {
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
    }
  }, [autocompleteOpen, isInputFocused, isPendingSuggestions, suggestions]);

  const handleLocationChange = (value: string) => {
    setHasUserInteracted(true);
    setInputValue(value);
    // Don't update location prop while typing - only update when a suggestion is selected
    // Close autocomplete if input is too short
    if (value.length < PLACES_AUTOCOMPLETE_MIN_CHARS) {
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
    }
    // Note: Autocomplete will open automatically when suggestions arrive (via useEffect)
  };

  const handleSelectPlace = (input: {
    description: string;
    placeId: string;
  }) => {
    setInputValue(input.description);
    setHasUserInteracted(false);

    // Always persist placeIds with location labels — never location-only (Geocoding cost).
    const applyLocationSearch =
      setLocationSearch ??
      (setLocation && setPlaceIds
        ? (nextLocations: string[], nextPlaceIds?: string[]) => {
            setLocation(nextLocations);
            setPlaceIds(nextPlaceIds);
          }
        : null);

    if (!applyLocationSearch) {
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
      inputRef.current?.blur();
      return;
    }

    // Always keep `placeIds[]` aligned with `location[]` by index.
    const next = addLocationSelection(
      location,
      placeIds,
      input.description,
      input.placeId,
    );
    // Re-selecting an already-resolved label changes nothing; skip the navigate.
    const changed =
      next.location.length !== (location?.length ?? 0) ||
      next.placeIds.some((id, index) => id !== (placeIds?.[index] ?? ""));
    if (changed) {
      applyLocationSearch(next.location, next.placeIds);
    }

    setAutocompleteOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!autocompleteOpen || suggestions.length === 0) {
      // Allow normal input behavior when autocomplete is closed
      return;
    }

    // Handle arrow keys to navigate suggestions
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1,
      );
    }
    // Handle Enter to select the highlighted suggestion
    else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSelectPlace({
          description: suggestions[selectedIndex].description,
          placeId: suggestions[selectedIndex].placeId,
        });
      } else if (suggestions.length > 0) {
        // If no item is selected, select the first one
        handleSelectPlace({
          description: suggestions[0].description,
          placeId: suggestions[0].placeId,
        });
      }
    }
    // Handle Escape to close
    else if (e.key === "Escape") {
      e.preventDefault();
      setAutocompleteOpen(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <form
      className={`${className ? className : ""} flex flex-col`}
      style={{ viewTransitionName: "search-bar" }}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formParams: FormParams = {
          location,
          placeIds,
          guests,
          type,
        };
        if (onSubmit) {
          onSubmit(formParams);
        }
      }}
    >
      <div className="text-white bg-black/60 p-5 flex flex-wrap lg:flex-nowrap gap-4 w-full justify-between rounded-md">
        <Popover open={autocompleteOpen} onOpenChange={setAutocompleteOpen}>
          <PopoverAnchor asChild>
            <div className="min-w-[210px] w-full grow relative">
              <Input
                ref={inputRef}
                size={size === "large" ? "large" : "default"}
                placeholder="Ort, PLZ, Bundesland"
                className="w-full"
                iconEnd={<PinIcon />}
                variant="flat"
                name="location"
                value={inputValue}
                disablePasswordManagers={true}
                onChange={(e) => handleLocationChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  setIsInputFocused(true);
                  // Focusing a prefilled value must not issue Autocomplete.
                  setHasUserInteracted(false);
                  // Open autocomplete if we already have suggestions
                  if (
                    inputValue.length >= PLACES_AUTOCOMPLETE_MIN_CHARS &&
                    suggestions.length > 0
                  ) {
                    setAutocompleteOpen(true);
                  }
                }}
                onBlur={() => {
                  setIsInputFocused(false);
                  setHasUserInteracted(false);
                }}
              />
            </div>
          </PopoverAnchor>
          <PopoverContent
            className="p-0"
            style={{ width: "var(--radix-popover-trigger-width)" }}
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => {
              setAutocompleteOpen(false);
              inputRef.current?.focus();
            }}
          >
            <Command
              shouldFilter={false}
              value={
                selectedIndex >= 0 && selectedIndex < suggestions.length
                  ? suggestions[selectedIndex].description
                  : undefined
              }
            >
              <CommandList>
                {isPendingSuggestions ? (
                  <div className="py-6 text-center text-sm">Lade...</div>
                ) : suggestions.length === 0 ? (
                  <CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {suggestions.map((suggestion) => (
                      <CommandItem
                        key={suggestion.placeId}
                        value={suggestion.description}
                        onSelect={() =>
                          handleSelectPlace({
                            description: suggestion.description,
                            placeId: suggestion.placeId,
                          })
                        }
                        className="cursor-pointer"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {suggestion.mainText}
                          </span>
                          {suggestion.secondaryText && (
                            <span className="text-xs text-muted-foreground">
                              {suggestion.secondaryText}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Select
          value={guests || "any"}
          name="guests"
          onValueChange={(e) => setGuests?.(e as NumberOfGuests)}
        >
          <SelectTrigger
            className="min-w-[210px] w-full grow"
            customIcon={<UserIcon className="size-6" />}
            variant="shadow"
            size={size === "large" ? "large" : "default"}
          >
            <SelectValue placeholder="Gästezahl" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(NumberOfGuestsOptions).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={type !== null ? String(type) : ""}
          onValueChange={(e) => {
            if (e === "all") {
              setType?.(null);
            } else {
              setType?.(Number(e));
            }
          }}
          name="type"
        >
          <SelectTrigger
            className="min-w-[210px] w-full grow"
            variant="shadow"
            size={size === "large" ? "large" : "default"}
          >
            <SelectValue
              placeholder={`${isPendingEventTypes ? "Lade..." : "Anlass"}`}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Anlässe</SelectItem>
            {eventTypes?.map((type) => (
              <SelectItem key={type.id} value={`${type.id}`}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="submit"
          className={cn("min-w-[170px]", size === "large" ? "h-[48px]" : "")}
          size={size === "large" ? "lg" : "default"}
        >
          {isPending ? (
            <Spinner />
          ) : (
            <>
              <AmountFormatted>{numberOfMatchingLocations}</AmountFormatted>{" "}
              Treffer
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
